import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { PollRead } from '@models/api/poll';
import { cancelEntityProducers } from '@lib/cache/entityProducers';
import { getQuestionEntity } from '@lib/cache/questions';
import {
  beginPollWrite,
  isLatestPollWrite,
  pollWriteScopeId,
  readPollQuestion,
  settlePollWrite,
  showPoll,
} from '@lib/cache/polls';
import { retryOnceUnlessClientError } from '@lib/mutationRetry';
import { pollErrorCode } from '@lib/polls/pollErrors';
import { POLL_COPY } from '@components/polls/pollCopy';
import { toast } from '@hooks/use-toast';

/** The block as cached now (what every card shows), or the one the action was taken on. */
export const currentPollBlock = (
  queryClient: QueryClient,
  questionId: string,
  base: PollRead,
): PollRead => getQuestionEntity(queryClient, questionId)?.poll ?? base;

export interface PollBlockWriteVariables {
  /** The block the action was taken on; the cached one wins when there is one. */
  base: PollRead;
}

export interface UsePollBlockWriteOptions<V extends PollBlockWriteVariables> {
  questionId: string;
  /** For the re-read after a refusal that means the card is out of date. */
  slug: string | null;
  send: (questionId: string, variables: V) => Promise<PollRead>;
  /** What the card shows until the server answers; null shows nothing early. */
  predict: (current: PollRead, variables: V) => PollRead | null;
  /** A 409 that says the card is stale (already closed, already open): re-read it, no toast. */
  staleCode: string;
  onSaved?: () => void;
}

/**
 * A write that replaces a poll's whole block from the server's answer: End and Reopen (TECH-197
 * § Time and ending; CONTRACT § 4.4). In the poll's one write queue with its answers
 * (`pollWriteScopeId`), so the server ends on the last action; the guess shows at once in every
 * cached copy, a read in flight is cancelled first so it cannot put the old block back, and only
 * the newest write's outcome is shown (`pollWriteOrder`). A failure goes back to what the server
 * last confirmed with the generic error toast; `staleCode` re-reads the poll instead.
 */
export const usePollBlockWrite = <V extends PollBlockWriteVariables>({
  questionId,
  slug,
  send,
  predict,
  staleCode,
  onSaved,
}: UsePollBlockWriteOptions<V>) => {
  const queryClient = useQueryClient();

  return useMutation<PollRead, unknown, V, { ticket: number }>({
    scope: { id: pollWriteScopeId(questionId) },
    // A server error before the write lands is safe to send again; one after it answers 409
    // `staleCode`, which re-reads the poll to what the server holds.
    // At most one retry (~1 s): the guess on the card must not outlive a failure (E10).
    retry: retryOnceUnlessClientError,
    mutationFn: variables => send(questionId, variables),
    onMutate: async variables => {
      const ticket = beginPollWrite(
        questionId,
        currentPollBlock(queryClient, questionId, variables.base),
      );
      await cancelEntityProducers(queryClient, questionId);
      if (isLatestPollWrite(questionId, ticket)) {
        const guess = predict(currentPollBlock(queryClient, questionId, variables.base), variables);
        if (guess) showPoll(queryClient, questionId, guess);
      }
      return { ticket };
    },
    onSuccess: (poll, _variables, context) => {
      if (settlePollWrite(questionId, context.ticket, poll).latest) {
        showPoll(queryClient, questionId, poll);
      }
      onSaved?.();
    },
    onError: (error, _variables, context) => {
      if (!context) return;
      const { latest, confirmed } = settlePollWrite(questionId, context.ticket, null);
      if (!latest) return;
      if (confirmed) showPoll(queryClient, questionId, confirmed);
      if (pollErrorCode(error) === staleCode) {
        void readPollQuestion(queryClient, { id: questionId, slug }).catch(() => undefined);
        return;
      }
      toast({
        title: POLL_COPY.somethingWentWrong,
        variant: 'error',
        id: `poll-write-failed:${questionId}`,
      });
    },
  });
};
